// sfxSynth.js
export class SfxSynth {
  constructor(audioContext) {
    this.ctx =
      audioContext || new (window.AudioContext || window.webkitAudioContext)()
  }

  // generic tone burst
  burst({
    type = 'sine',
    startFreq = 800,
    endFreq = 200,
    duration = 0.15,
    volume = 0.4,
    noise = false,
    pitchSlideExp = 2.0,
  } = {}) {
    const ctx = this.ctx
    const sampleRate = ctx.sampleRate
    const totalSamples = Math.floor(sampleRate * duration)
    const dataL = new Float32Array(totalSamples)
    const dataR = new Float32Array(totalSamples)

    for (let i = 0; i < totalSamples; i++) {
      const t = i / totalSamples // 0..1
      const freqNow =
        startFreq + (endFreq - startFreq) * Math.pow(t, pitchSlideExp)

      // basic osc
      let sample
      if (noise) {
        sample = Math.random() * 2 - 1
      } else {
        const phase = 2 * Math.PI * freqNow * (i / sampleRate)
        switch (type) {
          case 'square':
            sample = Math.sign(Math.sin(phase))
            break
          case 'saw':
            sample =
              2 * (phase / (2 * Math.PI) - Math.floor(phase / (2 * Math.PI))) -
              1
            break
          default:
            sample = Math.sin(phase)
        }
      }

      // amplitude envelope: quick attack, quick decay
      const env = Math.min(t / 0.02, 1) * (1 - t) // pop then fade
      const v = sample * env * volume

      dataL[i] = v
      dataR[i] = v
    }

    // create buffer
    const buffer = ctx.createBuffer(2, totalSamples, sampleRate)
    buffer.copyToChannel(dataL, 0)
    buffer.copyToChannel(dataR, 1)
    return buffer
  }

  // helpers for specific game sounds:

  // 1. projectile fired
  blast() {
    return this.burst({
      type: 'square',
      startFreq: 1200,
      endFreq: 600,
      duration: 0.12,
      volume: 0.35,
      pitchSlideExp: 1.5,
    })
  }

  // 2. hit impact on enemy
  hit() {
    // noise pop with fast decay
    return this.burst({
      noise: true,
      startFreq: 200,
      endFreq: 80,
      duration: 0.09,
      volume: 0.5,
    })
  }

  // 3. enemy death
  enemyDie() {
    return this.burst({
      type: 'sine',
      startFreq: 500,
      endFreq: 120,
      duration: 0.25,
      volume: 0.4,
      pitchSlideExp: 2.5, // steeper slide = "bwoop"
    })
  }

  // 4. plant placed
  plantPlop() {
    return this.burst({
      type: 'sine',
      startFreq: 220,
      endFreq: 180,
      duration: 0.11,
      volume: 0.3,
      pitchSlideExp: 0.5, // gentle
    })
  }

  // 5. pickup resource
  pickup() {
    // two little chirps back to back
    const first = this.burst({
      type: 'sine',
      startFreq: 900,
      endFreq: 1200,
      duration: 0.07,
      volume: 0.35,
      pitchSlideExp: 0.5,
    })
    const second = this.burst({
      type: 'sine',
      startFreq: 1200,
      endFreq: 1500,
      duration: 0.07,
      volume: 0.35,
      pitchSlideExp: 0.5,
    })

    // stitch buffers together
    const ctx = this.ctx
    const totalLen = first.length + second.length
    const out = ctx.createBuffer(2, totalLen, ctx.sampleRate)

    for (let ch = 0; ch < 2; ch++) {
      const arr = new Float32Array(totalLen)
      first.copyFromChannel(arr, ch)
      const tmp = new Float32Array(second.length)
      second.copyFromChannel(tmp, ch)
      arr.set(tmp, first.length)
      out.copyToChannel(arr, ch)
    }
    return out
  }

  play(buffer) {
    const src = this.ctx.createBufferSource()
    src.buffer = buffer
    src.connect(this.ctx.destination)
    src.start()
  }

  // === Menu sounds ===
  menuHover() {
    return this.burst({
      type: 'sine',
      startFreq: 900,
      endFreq: 1100,
      duration: 0.08,
      volume: 0.25,
      pitchSlideExp: 0.3,
    })
  }

  menuClick() {
    return this.burst({
      type: 'square',
      startFreq: 800,
      endFreq: 400,
      duration: 0.1,
      volume: 0.4,
      pitchSlideExp: 1.2,
    })
  }

  menuBack() {
    return this.burst({
      type: 'sine',
      startFreq: 500,
      endFreq: 300,
      duration: 0.12,
      volume: 0.3,
      pitchSlideExp: 1.8,
    })
  }

  menuOpen() {
    return this.burst({
      type: 'sine',
      startFreq: 200,
      endFreq: 800,
      duration: 0.3,
      volume: 0.3,
      pitchSlideExp: 0.7,
    })
  }

  menuClose() {
    return this.burst({
      type: 'sine',
      startFreq: 800,
      endFreq: 200,
      duration: 0.25,
      volume: 0.3,
      pitchSlideExp: 1.5,
    })
  }
}
